import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Clock, LinkExternal01, AlertCircle, Minimize02 } from '@untitledui/icons';
import { Tooltip } from '@/components/base/tooltip/tooltip';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { useI18n } from '@/hooks/use-i18n';
import type { Container } from '@/types/container';

function getRiskLevelColor(riskLevel: string): 'gray' | 'success' | 'warning' | 'error' {
    switch (riskLevel) {
        case 'Low': return 'success';
        case 'Medium': return 'warning';
        case 'High': return 'error';
        case 'Critical': return 'error';
        default: return 'gray';
    }
}

function getStatusColor(status: string): 'gray' | 'brand' | 'warning' | 'success' | 'error' {
    switch (status) {
        case 'Draft': return 'gray';
        case 'Booked': return 'brand';
        case 'In Transit': return 'brand';
        case 'Arrived': return 'warning';
        case 'Delivered': return 'success';
        case 'On Hold': return 'warning';
        case 'Cancelled': return 'error';
        default: return 'gray';
    }
}

export interface ContainerGlassDetailProps {
    container: Container;
    lastUpdateTime?: Date | null;
    onClose: () => void;
    /** Nasconde la card per vedere la rotta; clic sul marker la riapre */
    onCollapse?: () => void;
    collapseTooltip?: string;
    formatDateTime?: (date: Date) => string;
    /** Conteggio active issues dall'API (stesso della pagina dettaglio). Se fornito, la sezione "Active issues" usa questo valore. */
    activeIssuesCount?: number;
}

export function ContainerGlassDetail({
    container,
    lastUpdateTime,
    onClose,
    onCollapse,
    collapseTooltip = 'Hide to see route',
    formatDateTime = (d) => new Intl.DateTimeFormat(navigator.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(d),
    activeIssuesCount: activeIssuesCountFromApi,
}: ContainerGlassDetailProps) {
    const { translateContainerStatus, translateRiskLevel, translateDocumentStatus } = useI18n();
    const [timeAgo, setTimeAgo] = useState<string>('');

    useEffect(() => {
        if (!lastUpdateTime) {
            setTimeAgo('');
            return;
        }
        const updateTimeAgo = () => {
            const now = new Date();
            const diff = Math.floor((now.getTime() - lastUpdateTime.getTime()) / 1000);
            if (diff < 60) {
                setTimeAgo(`${diff} seconds ago`);
            } else if (diff < 3600) {
                const minutes = Math.floor(diff / 60);
                setTimeAgo(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`);
            } else {
                const hours = Math.floor(diff / 3600);
                setTimeAgo(`${hours} ${hours === 1 ? 'hour' : 'hours'} ago`);
            }
        };
        updateTimeAgo();
        const interval = setInterval(updateTimeAgo, 60000);
        return () => clearInterval(interval);
    }, [lastUpdateTime]);

    return (
        <div
            className="max-w-md w-full rounded-xl shadow-xl ring-1 ring-white/10 bg-primary/85 backdrop-blur-md overflow-hidden"
            role="dialog"
            aria-label="Container details"
        >
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10 flex-shrink-0">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                        <h2 className="text-xl font-semibold text-primary truncate">
                            {container.containerNumber || '—'}
                        </h2>
                        <Link
                            to={`/shipper/containers/${container.id}`}
                            className="flex-shrink-0 p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-white/10 transition-colors"
                            aria-label="Open container details"
                        >
                            <LinkExternal01 className="size-5" />
                        </Link>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {onCollapse && (
                            <Tooltip title={collapseTooltip} placement="bottom">
                                <button
                                    type="button"
                                    onClick={onCollapse}
                                    className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-white/10 transition-colors"
                                    aria-label={collapseTooltip}
                                >
                                    <Minimize02 className="size-5" />
                                </button>
                            </Tooltip>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-white/10 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                {/* 4 columns: Status, Progress, ETA, Risk level */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                    <div>
                        <div className="text-xs text-tertiary mb-1">Status</div>
                        <Badge color={getStatusColor(container.status)} size="sm">
                            {translateContainerStatus(container.status)}
                        </Badge>
                    </div>
                    <div>
                        <div className="text-xs text-tertiary mb-1">Progress</div>
                        <div className="text-sm font-medium text-primary">{container.progress}%</div>
                    </div>
                    <div>
                        <div className="text-xs text-tertiary mb-1">ETA</div>
                        <div className="text-sm text-primary">
                            {container.eta ? container.eta.toLocaleDateString(navigator.language, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-tertiary mb-1">Risk level</div>
                        <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                            {translateRiskLevel(container.riskLevel)}
                        </Badge>
                    </div>
                </div>
                {/* Active issues: prefer API count (come in dettaglio container), altrimenti da container */}
                {(activeIssuesCountFromApi != null && activeIssuesCountFromApi > 0) ||
                (activeIssuesCountFromApi == null && (container.documentStatus !== 'Completo' || container.hasActiveAlerts || (container.alertCount != null && container.alertCount > 0))) ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <AlertCircle className="size-4 text-warning-500 flex-shrink-0" />
                        <span className="text-xs text-tertiary">Active issues:</span>
                        {activeIssuesCountFromApi != null && activeIssuesCountFromApi > 0 ? (
                            <Badge color="warning" size="sm">
                                {activeIssuesCountFromApi} {activeIssuesCountFromApi === 1 ? 'issue' : 'issues'}
                            </Badge>
                        ) : (
                            <>
                                {container.documentStatus !== 'Completo' && (
                                    <Badge color={container.documentStatus === 'Error' ? 'error' : 'warning'} size="sm">
                                        Docs: {translateDocumentStatus(container.documentStatus)}
                                    </Badge>
                                )}
                                {container.hasActiveAlerts && (
                                    <Badge color="warning" size="sm">
                                        {container.alertCount != null && container.alertCount > 0 ? `${container.alertCount} alert(s)` : 'Alerts'}
                                    </Badge>
                                )}
                            </>
                        )}
                    </div>
                ) : null}
            </div>

            {/* Journey: from — progress bar — to */}
            <div className="px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 text-left min-w-0">
                        <div className="text-xs text-tertiary">Departure</div>
                        <div className="text-sm font-medium text-primary truncate">{container.route.from}</div>
                    </div>
                    <div className="flex-1 min-w-0 px-2">
                        <ProgressBar value={container.progress} min={0} max={100} />
                    </div>
                    <div className="flex-shrink-0 text-right min-w-0">
                        <div className="text-xs text-tertiary">Arrival</div>
                        <div className="text-sm font-medium text-primary truncate">{container.route.to}</div>
                        {container.eta && (
                            <div className="text-xs text-tertiary">
                                {container.eta.toLocaleDateString(navigator.language, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer: Received X ago */}
            <div className="px-6 py-3 flex items-center gap-2 text-xs text-tertiary">
                <Clock className="size-4 flex-shrink-0" />
                <span>
                    {timeAgo ? `Received: ${timeAgo}` : lastUpdateTime ? formatDateTime(lastUpdateTime) : 'No tracking data'}
                </span>
            </div>
        </div>
    );
}
